import { Component } from 'react'
import { captureException } from '../sentry.js'

function isChunkLoadError(error) {
  if (!error) return false
  const msg = error.message || ''
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('Importing a module script failed')
  )
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    console.error('Baulit caught an error:', error, info.componentStack)

    // Report to Sentry (chunk errors are filtered in sentry.js beforeSend)
    captureException(error, { componentStack: info.componentStack })

    // Auto-reload on stale chunk errors (caused by new deployments)
    if (isChunkLoadError(error)) {
      const key = 'baulit_chunk_reload'
      const lastReload = sessionStorage.getItem(key)
      const now = Date.now()
      // Only auto-reload once per 30 seconds to avoid infinite loops
      if (!lastReload || now - Number(lastReload) > 30000) {
        sessionStorage.setItem(key, String(now))
        window.location.reload()
        return
      }
    }
  }

  render() {
    if (this.state.hasError) {
      const isChunk = isChunkLoadError(this.state.error)

      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm max-w-md w-full p-8 text-center">
            <div className="text-4xl mb-4">{isChunk ? '🔄' : '⚠️'}</div>
            <h1 className="text-lg font-bold text-slate-800 mb-2">
              {isChunk ? 'App Updated' : 'Something went wrong'}
            </h1>
            <p className="text-sm text-slate-500 mb-6">
              {isChunk
                ? 'A new version of Baulit was deployed. Please refresh to load the latest version.'
                : 'An unexpected error occurred. Your data is safe — try refreshing the page.'}
            </p>
            {!isChunk && this.state.error && (
              <pre className="text-xs text-left bg-slate-50 border border-slate-200 rounded-lg p-3 mb-6 overflow-auto max-h-32 text-red-600">
                {this.state.error.message}
              </pre>
            )}
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white text-sm font-medium px-6 py-2.5 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
